package com.kinoshazam.uiKit.components.videoItem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.basicMarquee
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.theme.Large
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.uiKit.theme.largeRadius
import com.kinoshazam.uiKit.theme.middleRadius
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.arrow_right
import org.jetbrains.compose.resources.painterResource

/**
 * Trailing “watch all” card: solid poster panel with a compact arrow badge
 * instead of stretching the icon across the whole card.
 * Click/focus comes from the parent wrapper (e.g. TV Card).
 */
@OptIn(ExperimentalFoundationApi::class)
@Composable
fun MediumWatchAllItem(
    title: String,
    isFocused: Boolean = false,
    modifier: Modifier = Modifier,
    width: Dp = MediumVideoCardWidth,
    showFocusBorder: Boolean = true,
    onClick: (() -> Unit)? = null,
) {
    val highlighted = isFocused
    val titleStyle = if (width < MediumVideoCardWidth) Medium else Large
    val corner = if (width < MediumVideoCardWidth) middleRadius else largeRadius
    val borderWidth = if (width < MediumVideoCardWidth) 3.dp else 4.dp
    val iconSize = if (width < MediumVideoCardWidth) 22.dp else 28.dp
    val badgeSize = if (width < MediumVideoCardWidth) 48.dp else 64.dp

    Column(
        modifier = modifier
            .width(width)
            .wrapContentHeight()
            .clip(shape = corner)
            .then(
                if (onClick != null) {
                    Modifier.clickable(onClick = onClick)
                } else {
                    Modifier
                }
            )
    ) {
        val posterModifier = Modifier
            .fillMaxWidth()
            .aspectRatio(MediumVideoPosterAspectRatio)
            .clip(corner)
            .background(UiKitTheme.colors.color_4)

        Box(
            modifier = if (highlighted && showFocusBorder) {
                posterModifier.border(
                    border = BorderStroke(borderWidth, UiKitTheme.colors.color_1),
                    shape = corner,
                )
            } else {
                posterModifier
            },
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(badgeSize)
                    .clip(CircleShape)
                    .background(
                        if (highlighted) UiKitTheme.colors.color_1.copy(alpha = 0.25f)
                        else UiKitTheme.colors.color_5.copy(alpha = 0.55f)
                    ),
                contentAlignment = Alignment.Center,
            ) {
                Image(
                    painter = painterResource(Res.drawable.arrow_right),
                    contentDescription = null,
                    colorFilter = ColorFilter.tint(
                        if (highlighted) UiKitTheme.colors.color_1 else UiKitTheme.colors.color_2
                    ),
                    modifier = Modifier.size(iconSize),
                )
            }
        }

        Text(
            text = title,
            color = if (highlighted) UiKitTheme.colors.color_1 else UiKitTheme.colors.color_2,
            maxLines = 1,
            overflow = if (highlighted) TextOverflow.Clip else TextOverflow.Ellipsis,
            style = titleStyle,
            softWrap = false,
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 32.dp)
                .padding(horizontal = 4.dp, vertical = 4.dp)
                .then(if (highlighted) Modifier.basicMarquee() else Modifier),
        )
    }
}
